function debounce(fn, wait) {
  let t;
  return function(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.rangeInputs = this.querySelectorAll('.range-input input');
    this.rangeSlider = this.querySelector('.slider-container .price-slider');
    this.minPriceText = this.querySelector('.min_price');
    this.maxPriceText = this.querySelector('.max_price');
    this.currencySymbol = this.querySelector('.price-range-main').getAttribute('currency-symbol');
    this.debouncedTriggerFilterUpdate = debounce(() => this.triggerFilterUpdate(), 500);
    this.init();
  }

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlMin = urlParams.get('filter.v.price.gte');
    const urlMax = urlParams.get('filter.v.price.lte');

    const minVal = urlMin ? parseInt(urlMin, 10) : parseInt(this.rangeInputs[0].value, 10);
    const maxVal = urlMax ? parseInt(urlMax, 10) : parseInt(this.rangeInputs[1].value, 10);

    // Store initial values to detect if user has actually changed the filter
    this.initialMin = parseInt(this.rangeInputs[0].value, 10);
    this.initialMax = parseInt(this.rangeInputs[1].value, 10);

    this.updateUI(minVal, maxVal);
    this.bindEvents();
  }

  bindEvents() {
    this.rangeInputs.forEach((input) => {
      input.addEventListener('input', () => {
        const newMin = parseInt(this.rangeInputs[0].value, 10);
        const newMax = parseInt(this.rangeInputs[1].value, 10);

        if (newMin <= newMax) {
          this.updateUI(newMin, newMax, true); // Skip filter chips during slider movement
          this.debouncedTriggerFilterUpdate();
        }
      });
    });
  }

  updateUI(min, max, skipNewFilterCreation = false) {
    this.minPriceText.textContent = `${this.currencySymbol}${min}.00`;
    this.maxPriceText.textContent = `${this.currencySymbol}${max}.00`;
    this.rangeSlider.style.left = `${(min / this.rangeInputs[0].max) * 100}%`;
    this.rangeSlider.style.right = `${100 - ((max / this.rangeInputs[1].max) * 100)}%`;
    this.rangeInputs[0].value = min;
    this.rangeInputs[1].value = max;

    // Check and create filter elements if they don't exist
    this.ensureFilterElementsExist(min, max, skipNewFilterCreation);
  }

  ensureFilterElementsExist(min, max, skipNewFilterCreation = false) {
    // Find the active filter group container
    const activeFilterGroup = document.querySelector('.active-filter-group__item-wrapper');

    if (!activeFilterGroup) {
      return;
    }

    // Check if there are any active filters (price range or other filters)
    // A price filter is active if the user has changed from the default range
    const hasActivePriceFilter = ((min !== this.initialMin) || (max !== this.initialMax));

    // Handle price range filter - create or update
    const existingPriceFilter = activeFilterGroup.querySelector('[data-filter-type="price-range"]');

    // Check for other active filters (excluding the price range filter we might create)
    const hasOtherActiveFilters = (activeFilterGroup.querySelectorAll('.filter.active-filter-item:not(.active-filter-clear-all):not([data-filter-type="price-range"])').length > 0);
    const hasAnyActiveFilters = hasActivePriceFilter || hasOtherActiveFilters;

    if (hasActivePriceFilter) {
      if (existingPriceFilter) {
        // Update existing price range filter with new values
        this.updatePriceRangeFilter(existingPriceFilter, min, max);
      } else if (!skipNewFilterCreation) {
        // Create new price range filter (skip during slider movement to avoid duplicates)
        this.createPriceRangeFilter(activeFilterGroup, min, max);
      }
    } else if (existingPriceFilter) {
      // Remove price range filter if no longer active
      existingPriceFilter.remove();
    }

    // Only show clear all button if there are active filters
    const clearAllButton = activeFilterGroup.querySelector('.active-filter-clear-all');
    if (hasAnyActiveFilters && !clearAllButton) {
      this.createClearAllButton(activeFilterGroup);
    } else if (!hasAnyActiveFilters && clearAllButton) {
      // Remove clear all button if no active filters
      clearAllButton.remove();
    }
  }

  createPriceRangeFilter(container, min, max) {
    const filterDiv = document.createElement('div');
    filterDiv.className = 'filter active-filter-item';
    filterDiv.setAttribute('data-filter-type', 'price-range');

    const span = document.createElement('span');
    span.textContent = `Price: ${this.currencySymbol}${min}.00 - ${this.currencySymbol}${max}.00`;

    const closeDiv = document.createElement('div');
    closeDiv.className = 'filter-close';
    closeDiv.setAttribute('data-render-section-url', this.getRemovePriceFilterUrl());

    // Add close icon (you may need to adjust this based on your icon implementation)
    closeDiv.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    filterDiv.appendChild(span);
    filterDiv.appendChild(closeDiv);

    // Insert before clear all button if it exists, otherwise append
    const clearAllButton = container.querySelector('.active-filter-clear-all');
    if (clearAllButton) {
      container.insertBefore(filterDiv, clearAllButton);
    } else {
      container.appendChild(filterDiv);
    }
  }

  updatePriceRangeFilter(existingFilter, min, max) {
    // Update the span text with new price values
    const span = existingFilter.querySelector('span');
    if (span) {
      span.textContent = `Price: ${this.currencySymbol}${min}.00 - ${this.currencySymbol}${max}.00`;
    }

    // Update the close button URL
    const closeDiv = existingFilter.querySelector('.filter-close');
    if (closeDiv) {
      closeDiv.setAttribute('data-render-section-url', this.getRemovePriceFilterUrl());
    }
  }

  createClearAllButton(container) {
    const clearAllDiv = document.createElement('div');
    clearAllDiv.className = 'filter active-filter-item active-filter-clear-all';
    clearAllDiv.setAttribute('data-render-section-url', window.location.pathname);

    const span = document.createElement('span');
    span.textContent = 'Clear all filters';

    clearAllDiv.appendChild(span);
    container.appendChild(clearAllDiv);
  }

  triggerFilterUpdate() {
    // Find the collection-info component
    const collectionInfo = document.querySelector('collection-info');
    if (!collectionInfo) {
      return;
    }

    // Get the form that contains the price range inputs
    const form = this.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    if (!form) {
      return;
    }

    // Create form data from the form
    const formData = new FormData(form);
    let searchParams = new URLSearchParams(formData).toString();

    // Preserve search query if it exists
    if (window.location.search.includes('?q=')) {
      const existingParams = new URLSearchParams(window.location.search);
      const qValue = existingParams.get('q');
      searchParams = `q=${qValue}&${searchParams}`;
    }

    // Use the collection-info's fetchSection method to update products
    collectionInfo.fetchSection(searchParams);
  }

  getRemovePriceFilterUrl() {
    // Get current URL and remove price filter parameters
    const url = new URL(window.location);
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    return url.toString();
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}

